// .ts extension: these modules are in the node --test import graph, which resolves literally
import type { IzAnswer, IzQuestion } from './izteach.ts';
import { answerList, toBoardLevel } from './izteach.ts';
import type { QuestionBankLevel } from './types.ts';

/**
 * IZ question types the board shows and opens with their own model: fill-blank, group, true-false-choice.
 *
 * Kept out of QuestionBankItem on purpose - the store's edit, delete and lesson paths only ever look
 * up `items`, so a row built here cannot reach toPayload or a PUT. The ones marked `editable` are
 * written through rich/ instead, which re-reads the stored row before building any request. Shapes as the IZ admin writes them:
 * fill-blank `correct` is free text ("1. a 2. b", "a;b", alternatives joined by "//"); group `questions`
 * is a list of *-child ids fetched with /question/admin?id_in=; true-false-choice `correct` maps each
 * answer key to true (Đúng) or false (Sai). direct-fill-blank-group is a fill-blank whose answers live
 * in direct-fill-blank-child rows (`config.key` is the blank number, `correct` the answer).
 */

export type ViewOnlyKind = 'fill-blank' | 'group' | 'true-false-choice';

export const VIEW_ONLY_TYPE_LABELS: Record<ViewOnlyKind, string> = {
  'fill-blank': 'Điền khuyết',
  group: 'Câu chùm',
  'true-false-choice': 'Đúng - Sai nhiều ý',
};

const OTHER_TYPE_LABELS: Record<string, string> = {
  matrix: 'Ma trận',
  'drag-drop': 'Kéo thả',
  'true-false-choice-child': 'Ý Đúng - Sai của câu chùm',
};

/** IZ types the board edits itself; everything else lands here. */
const BOARD_IZ_TYPES = ['single-choice', 'multiple-choice', 'text'];

export interface FillBlankAnswer {
  /** 1-based blank number. */
  number: number;
  text: string;
}

export interface ViewChoice {
  /** HTML, sanitized at render. */
  content: string;
  isCorrect: boolean;
}

export interface ViewStatement {
  key: string;
  /** HTML, sanitized at render. */
  content: string;
  /** Đúng / Sai, or null when the key is missing from `correct`. */
  correct: boolean | null;
}

/** The answerable part of a question, shared by top-level rows and group children. */
export type ViewBody =
  | { kind: 'choice'; multiple: boolean; content: string; options: ViewChoice[] }
  | { kind: 'text'; content: string }
  | { kind: 'fill-blank'; content: string; answers: FillBlankAnswer[] | null; rawCorrect: string }
  | { kind: 'true-false-choice'; content: string; statements: ViewStatement[] };

export type ViewChild = ViewBody & { id: string; explanation?: string };

interface ViewMeta {
  id: string;
  level: QuestionBankLevel;
  explanation?: string;
  updatedAt: string;
  /** The board can edit and delete it (rich/); a group or direct row only once its children are in and all known. */
  editable: boolean;
}

export type ViewOnlyQuestion =
  /** `answerIds`/`label` only on direct-fill-blank-group: answers arrive with the children. */
  | (ViewMeta & { kind: 'fill-blank'; content: string; answers: FillBlankAnswer[] | null; rawCorrect: string; answerIds?: string[]; label?: string })
  | (ViewMeta & { kind: 'true-false-choice'; content: string; statements: ViewStatement[] })
  /** `children` is null until the id_in request lands, and stays null when it failed. */
  | (ViewMeta & { kind: 'group'; content: string; childIds: string[]; children: ViewChild[] | null })
  | { kind: 'other'; id: string; typeLabel: string; text: string };

/** A view-only row the board can open as a quiz; 'other' has nothing to paint. */
export type OpenableViewOnly = Exclude<ViewOnlyQuestion, { kind: 'other' }>;

export function isOpenable(question: ViewOnlyQuestion): question is OpenableViewOnly {
  return question.kind !== 'other';
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function explainOf(raw: IzQuestion): string {
  return isRecord(raw.config) ? asString(raw.config.explain).trim() : '';
}

function plainText(html: string): string {
  return html.replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
}

/** `//` separates accepted alternatives in IZ fill-blank keys. */
function alternatives(text: string): string {
  return text.split('//').map((part) => part.trim()).filter((part) => part !== '').join(' / ');
}

/**
 * Per-blank answers out of the free-text key: "1. giữ gìn 2. sạch đẹp", "1;1", or one bare answer.
 * Null when there is nothing to show; a key that is not numbered or split comes back as one answer.
 */
export function parseFillBlankAnswers(correct: unknown): FillBlankAnswer[] | null {
  const text = plainText(asString(correct));
  if (!text) return null;
  if (/^1\s*[.):]/.test(text)) {
    // numbers must run 1, 2, 3... in order, each after whitespace, so "2 giờ" inside an answer stays text
    const starts: { number: number; at: number; body: number }[] = [];
    let from = 0;
    for (let number = 1; number < 100; number += 1) {
      const pattern = new RegExp('(^|\\s)' + number + '\\s*[.):]\\s*', 'g');
      pattern.lastIndex = from;
      const match = pattern.exec(text);
      if (!match) break;
      const at = match.index + match[1].length;
      starts.push({ number, at, body: match.index + match[0].length });
      from = match.index + match[0].length;
    }
    const answers = starts
      .map((start, index) => ({
        number: start.number,
        text: alternatives(text.slice(start.body, index + 1 < starts.length ? starts[index + 1].at : text.length)),
      }))
      .filter((answer) => answer.text !== '');
    if (answers.length > 0) return answers;
  }
  const parts = text.indexOf(';') >= 0 ? text.split(';') : [text];
  const answers = parts
    .map((part, index) => ({ number: index + 1, text: alternatives(part) }))
    .filter((answer) => answer.text !== '');
  return answers.length > 0 ? answers : null;
}

// numbered: {{ 1 }} (the admin sometimes leaves a stray brace) or (1); unnumbered: (____), ____, …..
const BLANK_PATTERN = /\{\{\s*(\d{1,2})\s*\}\}\}?|(^|[\s>;:,.(\[-])\((\d{1,2})\)|\(\s*_{3,}\s*\)|_{4,}|…[….]+|\.{4,}/g;

/**
 * Replaces every blank marker in the text of `html` (never inside a tag) with `fill(number)`.
 * Returns the new html and the blank numbers in reading order.
 */
export function markBlanks(html: string, fill: (number: number) => string): { html: string; numbers: number[] } {
  const numbers: number[] = [];
  let unnumbered = 0;
  const out = html.split(/(<[^>]*>)/).map((part) => {
    if (part.charAt(0) === '<') return part;
    return part.replace(BLANK_PATTERN, (_match: string, curly?: string, lead?: string, paren?: string) => {
      let number: number;
      let prefix = '';
      if (curly) number = Number(curly);
      else if (paren) {
        number = Number(paren);
        prefix = lead || '';
      } else {
        unnumbered += 1;
        number = unnumbered;
      }
      numbers.push(number);
      return prefix + fill(number);
    });
  });
  return { html: out.join(''), numbers };
}

/** Escapes text for splicing into html that is sanitized afterwards anyway. */
export function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** Passage html with every blank as a numbered box, filled with its answer once revealed. */
export function fillBlankHtml(html: string, answers: FillBlankAnswer[] | null, revealed: boolean): string {
  const byNumber: Record<number, string> = {};
  (answers || []).forEach((answer) => { byNumber[answer.number] = answer.text; });
  return markBlanks(html, (number) => {
    const text = revealed ? byNumber[number] : undefined;
    const no = '<span class="question-bank-blank__no">' + number + '</span>';
    return text
      ? '<span class="question-bank-blank is-filled">' + no + escapeHtml(text) + '</span>'
      : '<span class="question-bank-blank">' + no + '</span>';
  }).html;
}

/** Answer keys ride in stored order; a key missing from `correct` stays unknown rather than Sai. */
function statementsOf(answers: IzAnswer[], correct: unknown): ViewStatement[] {
  const map = isRecord(correct) ? correct : {};
  return answers.map((answer) => {
    const value = map[answer.key];
    return { key: answer.key, content: answer.value, correct: typeof value === 'boolean' ? value : null };
  });
}

function correctKeyList(correct: unknown): string[] {
  if (typeof correct === 'string') return correct ? [correct] : [];
  if (!Array.isArray(correct)) return [];
  return correct.map((entry) => (typeof entry === 'string' ? entry : isRecord(entry) ? asString(entry.key) : '')).filter((key) => key !== '');
}

/** The answerable body of an IZ question, top-level or `*-child`; null for types with no shape here. */
export function viewBodyFromIz(raw: IzQuestion): ViewBody | null {
  const base = asString(raw.type).replace(/-child$/, '');
  const content = asString(raw.html);
  if (base === 'single-choice' || base === 'multiple-choice') {
    const keys = correctKeyList(raw.correct);
    const options = answerList(raw.answers).map((answer) => ({ content: answer.value, isCorrect: keys.indexOf(answer.key) >= 0 }));
    return { kind: 'choice', multiple: base === 'multiple-choice', content, options };
  }
  if (base === 'text') return { kind: 'text', content };
  if (base === 'fill-blank') {
    return { kind: 'fill-blank', content, answers: parseFillBlankAnswers(raw.correct), rawCorrect: plainText(asString(raw.correct)) };
  }
  if (base === 'true-false-choice') {
    return { kind: 'true-false-choice', content, statements: statementsOf(answerList(raw.answers), raw.correct) };
  }
  return null;
}

function idOf(raw: IzQuestion): string {
  return asString(raw.id) || asString(raw._id);
}

function childIdsOf(raw: IzQuestion): string[] {
  const list = raw.questions;
  return Array.isArray(list)
    ? list.map((entry) => (typeof entry === 'string' ? entry : isRecord(entry) ? asString(entry.id) || asString(entry._id) : '')).filter((value) => value !== '')
    : [];
}

/** direct-fill-blank-child rows as numbered answers, in blank order. */
function directAnswers(ids: string[], byId: Record<string, IzQuestion>): FillBlankAnswer[] {
  const answers: FillBlankAnswer[] = [];
  ids.forEach((id, index) => {
    const raw = byId[id];
    if (!raw) return;
    const key = Number(isRecord(raw.config) ? asString(raw.config.key) : '');
    const text = alternatives(plainText(asString(raw.correct)));
    if (text) answers.push({ number: key > 0 ? key : index + 1, text });
  });
  return answers.sort((a, b) => a.number - b.number);
}

/** The IZ row as a view-only question, or null when it is a type the board edits itself. */
export function toViewOnlyQuestion(raw: IzQuestion): ViewOnlyQuestion | null {
  const id = idOf(raw);
  if (!id || BOARD_IZ_TYPES.indexOf(raw.type) >= 0) return null;
  const meta: ViewMeta = { id, level: toBoardLevel(raw.level), updatedAt: asString(raw.updatedAt), editable: raw.type === 'fill-blank' || raw.type === 'true-false-choice' };
  const explanation = explainOf(raw);
  if (explanation) meta.explanation = explanation;
  const content = asString(raw.html);

  if (raw.type === 'group') return { ...meta, kind: 'group', content, childIds: childIdsOf(raw), children: null };
  if (raw.type === 'direct-fill-blank-group') {
    return { ...meta, kind: 'fill-blank', content, answers: null, rawCorrect: '', answerIds: childIdsOf(raw), label: 'Điền trực tiếp' };
  }
  if (raw.type === 'fill-blank' || raw.type === 'true-false-choice') {
    const body = viewBodyFromIz(raw);
    if (body && body.kind === 'fill-blank') return { ...meta, kind: 'fill-blank', content, answers: body.answers, rawCorrect: body.rawCorrect };
    if (body && body.kind === 'true-false-choice') return { ...meta, kind: 'true-false-choice', content, statements: body.statements };
  }
  // html is the question itself; title is often IZ's default 'Tên Bài Kiểm Tra'
  const text = plainText(content) || asString(raw.title).trim();
  return { kind: 'other', id, typeLabel: OTHER_TYPE_LABELS[raw.type] || 'Dạng khác', text: text || '(Không có nội dung xem trước)' };
}

/** Child types rich/ has an editor for. */
const GROUP_CHILD_TYPES = ['single-choice-child', 'multiple-choice-child', 'fill-blank-child', 'true-false-choice-child'];
const DIRECT_CHILD_TYPES = ['direct-fill-blank-child'];

function allOfType(ids: string[], byId: Record<string, IzQuestion>, types: string[]): boolean {
  return ids.every((id) => Boolean(byId[id]) && types.indexOf(byId[id].type) >= 0);
}

/** Every child id the groups on a page point at, once each, for one id_in request. */
export function groupChildIds(questions: ViewOnlyQuestion[]): string[] {
  const out: string[] = [];
  questions.forEach((question) => {
    const ids = question.kind === 'group' ? question.childIds : question.kind === 'fill-blank' ? question.answerIds || [] : [];
    ids.forEach((id) => { if (out.indexOf(id) === -1) out.push(id); });
  });
  return out;
}

/**
 * Hangs fetched children on their groups in the group's own order (id_in answers unordered).
 * `fetched` null means the request failed, and the groups keep `children: null`.
 */
export function attachGroupChildren(questions: ViewOnlyQuestion[], fetched: IzQuestion[] | null): ViewOnlyQuestion[] {
  if (!fetched) return questions;
  const byId: Record<string, IzQuestion> = {};
  fetched.forEach((raw) => { const id = idOf(raw); if (id) byId[id] = raw; });
  return questions.map((question) => {
    if (question.kind === 'fill-blank' && question.answerIds) {
      const editable = allOfType(question.answerIds, byId, DIRECT_CHILD_TYPES);
      const answers = directAnswers(question.answerIds, byId);
      if (answers.length === 0) return { ...question, editable };
      return { ...question, editable, answers, rawCorrect: answers.map((answer) => answer.number + '. ' + answer.text).join(' ') };
    }
    if (question.kind !== 'group') return question;
    const children: ViewChild[] = [];
    question.childIds.forEach((id) => {
      const raw = byId[id];
      const body = raw ? viewBodyFromIz(raw) : null;
      if (!raw || !body) return;
      const child: ViewChild = { ...body, id };
      const explanation = explainOf(raw);
      if (explanation) child.explanation = explanation;
      children.push(child);
    });
    return { ...question, children, editable: allOfType(question.childIds, byId, GROUP_CHILD_TYPES) };
  });
}
